import {
  MapContainer,
  TileLayer,
  LayersControl,
  Polyline,
  ZoomControl,
} from "react-leaflet";
import PropTypes from "prop-types";
import polyUtil from "polyline-encoded";
import L from "leaflet";
import FullscreenControl from "./FullscreenControl";
import RouteYouLogoControl from "./RouteYouLogoControl";
import {
  DETAIL_MAP_LOADING_KEYS,
  DetailMapLoadingProvider,
  useDetailMapLoading,
} from "./DetailMapLoadingContext";
import "./DetailMap.scss";

const getDetailMapLoadingLabels = (loadingLabels = {}) => ({
  [DETAIL_MAP_LOADING_KEYS.surroundingRoutes]:
    loadingLabels.surroundingRoutes,
});

const DetailMapLoadingStatus = ({ loadingLabels }) => {
  const { loadingStates } = useDetailMapLoading();
  const labelsByKey = getDetailMapLoadingLabels(loadingLabels);
  const activeLoadingKeys = Object.keys(loadingStates).filter(
    (loadingKey) => loadingStates[loadingKey]
  );

  if (activeLoadingKeys.length === 0) {
    return null;
  }

  return (
    <div
      className="c-routedetail-leaflet-map__status"
      aria-live="polite"
      role="status"
    >
      {activeLoadingKeys.map((loadingKey) => (
        <div
          key={loadingKey}
          className="c-routedetail-leaflet-map__status-item"
        >
          <span
            className="c-routedetail-leaflet-map__status-spinner"
            aria-hidden="true"
          />
          <span>{labelsByKey[loadingKey] || "Kaart laden"}</span>
        </div>
      ))}
    </div>
  );
};

DetailMapLoadingStatus.propTypes = {
  loadingLabels: PropTypes.shape({
    surroundingRoutes: PropTypes.string,
  }),
};

const DetailMap = ({
  geometry,
  loadingLabels,
  routeColor = "#C83732",
  children,
}) => {
  if (!geometry) {
    return (
      <div className="c-routedetail-leaflet-map c-routedetail-leaflet-map__empty">
        Geen routegegevens beschikbaar
      </div>
    );
  }

  const positions = polyUtil.decode(geometry, 5);

  if (!positions || positions.length === 0) {
    return (
      <div className="c-routedetail-leaflet-map c-routedetail-leaflet-map__empty">
        Geen routegegevens beschikbaar
      </div>
    );
  }

  const bounds = L.latLngBounds(positions);

  return (
    <DetailMapLoadingProvider>
      <MapContainer
        bounds={bounds}
        minZoom={7}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom={true}
        className="c-routedetail-leaflet-map"
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Kaart">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satelliet">
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              maxZoom={20}
              subdomains={["mt1", "mt2", "mt3"]}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrein">
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
              maxZoom={20}
              subdomains={["mt1", "mt2", "mt3"]}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <ZoomControl position="bottomright" />
        <FullscreenControl />
        <RouteYouLogoControl />
          <DetailMapLoadingStatus loadingLabels={loadingLabels} />

        {children}

        <Polyline
          positions={positions}
          pathOptions={{ color: routeColor, weight: 7, opacity: 1 }}
        />
      </MapContainer>
    </DetailMapLoadingProvider>
  );
};

DetailMap.propTypes = {
  children: PropTypes.node,
  geometry: PropTypes.string,
  loadingLabels: PropTypes.shape({
    surroundingRoutes: PropTypes.string,
  }),
  routeColor: PropTypes.string,
};

export default DetailMap;
